import { Injectable, Injector } from '@angular/core';
import { IItem } from '@app/modules/mazars-common/interfaces/item.interface';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Pillar2ComponentBase } from '../pillar2-component-base';
import {
    BasisForAdjustmentsType,
    CheckResultStatus,
    FinancialAccountingStandard,
    FinancialYearType,
    JurisdictionRuleStatusType,
    GloBEParentType2,
    NotificationDataPointType,
    NotificationType,
    ObligationReturnType,
    ObligationStatus,
    ProjectsReferenceDataServiceProxy,
    ConsolidatedFinancialStatementsDto,
    SafeHarbourGirReportingSelection,
    ElectionInclusionType,
    PermanentSafeHarbourType,
} from '../pillar2-proxies';
import { CheckResultSettings } from './project-tab-healthcheck/check-result-settings';
import { ReferenceDataService } from '@app/modules/mazars-common/services/reference-data.service';
import { ProjectNotificationSetings } from './project-tab-notifications/project-notification-settings';
import { DueDateSettings } from '@app/modules/mazars-common/components/mazars-deadline-dates/due-date-settings';

@Injectable({
    providedIn: 'root',
})
export class ProjectReferenceDataService extends Pillar2ComponentBase {
    checkResultStatuses: IItem<number>[] = [];
    basisForAdjustments: IItem<number>[] = [];
    basisForAdjustmentsGloBEAllocationIncomeLoss: IItem<number>[] = [];
    basisForAdjustmentsACTCrossAllocationOfTaxes: IItem<number>[] = [];
    financialYears: IItem<number>[] = [];
    etrAndTutEtrAdjustmentArt: IItem<number>[] = [];
    etrAndTutFinancialAccountingStandard: IItem<number>[] = [];
    projectNotificationTypes: IItem<string>[] = [];
    projectNotificationDataPointTypes: IItem<string>[] = [];
    electionInclusions: IItem<number>[] = [];
    permanentSafeHarbourTypes: IItem<number>[] = [];

    constructor(
        injector: Injector,
        private projectsReferenceDataServiceProxy: ProjectsReferenceDataServiceProxy,
        private referenceService: ReferenceDataService,
    ) {
        super(injector);
    }

    onDestroy(): void {
        // Implement if necessary
    }

    public load() {
        this.getCheckResultStatuses()
            .pipe(take(1))
            .subscribe((statuses) => {
                statuses.pop();
                this.checkResultStatuses = statuses;
            });

        this.getBasisForAdjustmentsGloBEAllocationIncomeLoss()
            .pipe(take(1))
            .subscribe((basisForAdjustmentsGloBEAllocationIncomeLoss) => {
                this.basisForAdjustmentsGloBEAllocationIncomeLoss = basisForAdjustmentsGloBEAllocationIncomeLoss;
            });

        this.getBasisForAdjustmentsACTCrossAllocationOfTaxes()
            .pipe(take(1))
            .subscribe((basisForAdjustmentsACTCrossAllocationOfTaxes) => {
                this.basisForAdjustmentsACTCrossAllocationOfTaxes = basisForAdjustmentsACTCrossAllocationOfTaxes;
            });

        this.getFinancialYears()
            .pipe(take(1))
            .subscribe((financialYears) => {
                this.financialYears = financialYears;
            });

        this.getEtrAndTutEtrAdjustmentArt()
            .pipe(take(1))
            .subscribe((etrAndTutEtrAdjustmentArt) => {
                this.etrAndTutEtrAdjustmentArt = etrAndTutEtrAdjustmentArt;
            });

        this.getEtrAndTutFinancialAccountingStandard()
            .pipe(take(1))
            .subscribe((etrAndTutFinancialAccountingStandard) => {
                this.etrAndTutFinancialAccountingStandard = etrAndTutFinancialAccountingStandard;
            });
        this.getProjectNotificationTypes()
            .pipe(take(1))
            .subscribe((projectNotificationTypes) => {
                this.projectNotificationTypes = projectNotificationTypes;
            });
        this.getProjectNotificationDataPointTypes()
            .pipe(take(1))
            .subscribe((projectNotificationDataPointTypes) => {
                this.projectNotificationDataPointTypes = projectNotificationDataPointTypes;
            });

        this.getElectionInclusions()
            .pipe(take(1))
            .subscribe((electionInclusions) => {
                this.electionInclusions = electionInclusions;
            });

        this.getPermanentSafeHarbourTypes()
            .pipe(take(1))
            .subscribe((permanentSafeHarbourTypes) => {
                this.permanentSafeHarbourTypes = permanentSafeHarbourTypes;
            });
    }

    public getConsolidationMethods(): Observable<IItem<string>[]> {
        return this.projectsReferenceDataServiceProxy.getConsolidationMethods().pipe(
            take(1),
            map((methods) =>
                methods.map(
                    (method) =>
                        ({
                            id: method,
                            text: this.l('ConsolidationMethod' + method),
                        }) as IItem<string>,
                ),
            ),
        );
    }

    public getGloBEStatuses(): Observable<IItem<string>[]> {
        return this.projectsReferenceDataServiceProxy.getGloBEStatuses(GloBEParentType2.OrganizationalEntity).pipe(
            take(1),
            map((statuses) =>
                statuses.map(
                    (status) =>
                        ({
                            id: status,
                            text: this.l('GloBEStatus' + status),
                        }) as IItem<string>,
                ),
            ),
        );
    }

    public getCurrencies(): Observable<IItem<string>[]> {
        return this.referenceService.getCurrencies().pipe(
            take(1),
            map((currencies) =>
                currencies.map(
                    (currency) =>
                        ({
                            id: currency.id,
                            text: currency.text,
                        }) as IItem<string>,
                ),
            ),
        );
    }

    public getSafeHarbourGirReportingSelections(): IItem<number>[] {
        return Object.keys(SafeHarbourGirReportingSelection)
            .map((key) => SafeHarbourGirReportingSelection[key])
            .filter((value) => typeof value === 'string')
            .map(
                (status: string) =>
                    ({
                        id: SafeHarbourGirReportingSelection[status],
                        text: this.l(`SafeHarbourGirReporting_${status}`),
                    }) as IItem<number>,
            );
    }

    public getAllEntities(projectId: number): Observable<IItem<number>[]> {
        return this.projectsReferenceDataServiceProxy.getOrganizationalEntitiesWithNameAndNumber(projectId).pipe(
            take(1),
            map((result) =>
                (result?.items ?? [])
                    .slice()
                    .sort((a, b) => (a.entityNumber ?? '').localeCompare(b.entityNumber ?? ''))
                    .map((dto) => {
                        const optionalNumber = dto.entityNumber ? ` (${dto.entityNumber})` : '';
                        return {
                            id: dto.id,
                            text: `${dto.legalName}${optionalNumber}`,
                            source: dto,
                        } as IItem<number>;
                    }),
            ),
        );
    }

    private getCheckResultStatuses(): Observable<IItem<number>[]> {
        return of(
            Object.keys(CheckResultStatus)
                .map((key) => CheckResultStatus[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (status: string) =>
                        ({
                            id: CheckResultStatus[status],
                            text: this.l(`CheckResultStatus_${status}`),
                            icon: CheckResultSettings.getDataIcon(CheckResultStatus[status]),
                            iconColor: CheckResultSettings.getIconColor(CheckResultStatus[status]),
                        }) as IItem<number>,
                ),
        );
    }

    private getBasisForAdjustmentsGloBEAllocationIncomeLoss(): Observable<IItem<number>[]> {
        return of(
            Object.keys(BasisForAdjustmentsType)
                .map((key) => BasisForAdjustmentsType[key])
                .filter((value) => typeof value === 'string' && BasisForAdjustmentsType[value] < 10)
                .map(
                    (key: string) =>
                        ({
                            id: BasisForAdjustmentsType[key],
                            text: this.l(`BasisForAdjustmentsType_${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getBasisForAdjustmentsACTCrossAllocationOfTaxes(): Observable<IItem<number>[]> {
        return of(
            Object.keys(BasisForAdjustmentsType)
                .map((key) => BasisForAdjustmentsType[key])
                .filter((value) => typeof value === 'string' && BasisForAdjustmentsType[value] > 10 && BasisForAdjustmentsType[value] < 20)
                .map(
                    (key: string) =>
                        ({
                            id: BasisForAdjustmentsType[key],
                            text: this.l(`BasisForAdjustmentsType_${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getFinancialYears(): Observable<IItem<number>[]> {
        return of(
            Object.keys(FinancialYearType)
                .map((key) => FinancialYearType[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (key: string) =>
                        ({
                            id: FinancialYearType[key],
                            text: this.l(`FinancialYearType_${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getElectionInclusions(): Observable<IItem<number>[]> {
        return of(
            Object.keys(ElectionInclusionType)
                .map((key) => ElectionInclusionType[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (key: string) =>
                        ({
                            id: ElectionInclusionType[key],
                            text: this.l(`InclusionType_${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getPermanentSafeHarbourTypes(): Observable<IItem<number>[]> {
        return of(
            Object.keys(PermanentSafeHarbourType)
                .map((key) => PermanentSafeHarbourType[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (key: string) =>
                        ({
                            id: PermanentSafeHarbourType[key],
                            text: this.l(`PermanentSafeHarbourType_${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getEtrAndTutEtrAdjustmentArt(): Observable<IItem<number>[]> {
        return of(
            Object.keys(BasisForAdjustmentsType)
                .map((key) => BasisForAdjustmentsType[key])
                .filter((value) => typeof value === 'string' && BasisForAdjustmentsType[value] >= 100)
                .map(
                    (key: string) =>
                        ({
                            id: BasisForAdjustmentsType[key],
                            text: this.l(`BasisForAdjustmentsType_${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getEtrAndTutFinancialAccountingStandard(): Observable<IItem<number>[]> {
        return of(
            Object.keys(FinancialAccountingStandard)
                .map((key) => FinancialAccountingStandard[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (key: string) =>
                        ({
                            id: FinancialAccountingStandard[key],
                            text: this.l(`FinancialAccountingStandard${key}`),
                        }) as IItem<number>,
                ),
        );
    }

    private getProjectNotificationDataPointTypes(): Observable<IItem<string>[]> {
        return of(
            Object.keys(NotificationDataPointType)
                .map((key) => NotificationDataPointType[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (key: string) =>
                        ({
                            id: key,
                            text: this.l(`${key}`),
                        }) as IItem<string>,
                ),
        );
    }

    private getProjectNotificationTypes(): Observable<IItem<string>[]> {
        return of(
            Object.keys(NotificationType)
                .map((key) => NotificationType[key])
                .filter((value) => typeof value === 'string')
                .map(
                    (status: string) =>
                        ({
                            id: status,
                            text: this.l(`NotificationType_${status}`),
                            icon: ProjectNotificationSetings.getTypeIcon(NotificationType[status]),
                        }) as IItem<string>,
                ),
        );
    }

    public mapProjectDueDateStatuses(dueDateStatuses: string[]): IItem<string>[] {
        return dueDateStatuses.map((dueDateStatus) => {
            let status = {
                id: dueDateStatus,
                text: this.ld(`DueDateStatus_${dueDateStatus}`),
            } as IItem<string>;
            status.icon = DueDateSettings.getIcon(dueDateStatus);
            status.iconColor = DueDateSettings.getIconColor(dueDateStatus);
            status.tooltip = DueDateSettings.getIconTooltip(dueDateStatus);
            return status;
        });
    }

    public getObligationStatusesAsKey(): IItem<number>[] {
        return Object.keys(ObligationStatus)
            .map((key: any) => ObligationStatus[key])
            .filter((value) => typeof value === 'number')
            .map(
                (key: any) =>
                    ({
                        id: key,
                        text: this.l('ObligationStatus_' + ObligationStatus[key]),
                        icon: this.getObligationStatusIcon(key),
                    }) as IItem<number>,
            );
    }

    public getObligationStatuses(): IItem<string>[] {
        return Object.keys(ObligationStatus)
            .map((key: any) => ObligationStatus[key])
            .filter((value) => typeof value === 'number')
            .map(
                (key: any) =>
                    ({
                        id: ObligationStatus[key],
                        text: this.l('ObligationStatus_' + ObligationStatus[key]),
                        icon: this.getObligationStatusIcon(key),
                    }) as IItem<string>,
            );
    }

    public getObligationReturnTypes(): IItem<string>[] {
        return Object.keys(ObligationReturnType)
            .map((key: any) => ObligationReturnType[key])
            .filter((value) => typeof value === 'number')
            .map((key: any) => ({ id: ObligationReturnType[key], text: this.l('ObligationReturnType_' + ObligationReturnType[key]) }) as IItem<string>);
    }

    public getObligationReturnTypesAsKey(): IItem<number>[] {
        return Object.keys(ObligationReturnType)
            .map((key: any) => ObligationReturnType[key])
            .filter((value) => typeof value === 'number')
            .map((key: any) => ({ id: key, text: this.l('ObligationReturnType_' + ObligationReturnType[key]) }) as IItem<number>);
    }

    public getJurisdictionRuleStatusType(): IItem<number>[] {
        return Object.keys(JurisdictionRuleStatusType)
            .map((key: any) => JurisdictionRuleStatusType[key])
            .filter((value) => typeof value === 'number')
            .map(
                (key: any) =>
                    ({
                        id: key,
                        icon: this.getJurisdictionRuleStatusIcon(key),
                        text: this.l('JurisdictionsLegend_' + JurisdictionRuleStatusType[key]),
                    }) as IItem<number>,
            );
    }

    public getConsolidatedFinancialStatements(): IItem<number>[] {
        return Object.keys(ConsolidatedFinancialStatementsDto)
            .map((key: any) => ConsolidatedFinancialStatementsDto[key])
            .filter((value) => typeof value === 'number')
            .map(
                (key: any) =>
                    ({
                        id: key,
                        text: this.l('ConsolidatedFinancialStatementsDtoEnum' + ConsolidatedFinancialStatementsDto[key]),
                    }) as IItem<number>,
            );
    }
}
